import { useEffect, useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { PlatformLogo } from '@/components/PlatformLogo';
import { useToast } from '@/hooks/use-toast';
import {
  challengeAndVerifyWithFactorRefresh,
  isFactorNotFoundError,
} from '@/lib/mfaChallengeVerify';
import { NonProdMfaCodeBanner } from './NonProdMfaCodeBanner';

interface MFAVerificationProps {
  factorId: string;
  onSuccess: () => void;
  onCancel: () => void;
}

export function MFAVerification({ factorId, onSuccess, onCancel }: MFAVerificationProps) {
  const { toast } = useToast();
  const [code, setCode] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const lifecycleRef = useRef({ cancelled: false, completed: false, unmounted: false });

  useEffect(() => () => {
    lifecycleRef.current.unmounted = true;
  }, []);

  const handleCancel = () => {
    lifecycleRef.current.cancelled = true;
    void onCancel();
  };

  const completeVerifiedMfa = () => {
    const lifecycle = lifecycleRef.current;
    if (lifecycle.cancelled || lifecycle.completed || lifecycle.unmounted) return;

    lifecycle.completed = true;
    onSuccess();
  };

  const reconcileLateSuccess = () => {
    const lifecycle = lifecycleRef.current;
    if (lifecycle.cancelled) {
      // Supabase Auth can persist a late successful verify after the user's
      // first sign-out completes. Repeat the authoritative sign-out so the
      // explicit cancellation always wins over that stale response.
      void onCancel();
      return;
    }

    completeVerifiedMfa();
  };

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    if (code.length !== 6) return;

    setIsVerifying(true);
    try {
      // ENT-2459: `factorId` was listed when the step-up started and can be
      // stale by the time the code is submitted (admin reset, re-enrolment in
      // another tab). The helper refreshes it once on a factor-not-found so a
      // retry isn't aimed at a dead factor forever.
      await challengeAndVerifyWithFactorRefresh({
        factorId,
        code,
        // A timeout only releases the UI-owned await. Supabase can still
        // complete the outstanding verification later, at which point the
        // The timeout only releases the UI-owned await. A verified late
        // response must either finish this flow exactly once or re-assert an
        // explicit cancellation against Supabase's shared auth session.
        onLateSuccess: reconcileLateSuccess,
      });

      completeVerifiedMfa();
    } catch (error: any) {
      const lifecycle = lifecycleRef.current;
      if (lifecycle.cancelled || lifecycle.completed || lifecycle.unmounted) return;

      // A stale factor that could not be refreshed is not a bad code — saying
      // "invalid code" sends the user into a loop no code can escape.
      const description = isFactorNotFoundError(error)
        ? 'Your authenticator is no longer registered for this account. Cancel and sign in again to re-enrol.'
        : error?.message || 'Invalid verification code. Please try again.';

      toast({
        title: 'Verification failed',
        description,
        variant: 'destructive',
      });
      setCode('');
    } finally {
      if (!lifecycleRef.current.unmounted) setIsVerifying(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[110] bg-background">
      <div className="flex min-h-dvh items-center justify-center px-4 py-6 sm:px-6 lg:px-8 xl:px-10">
        <Card className="w-full max-w-md border-border/50 shadow-xl">
          <CardHeader className="pb-5 text-center sm:pb-6">
            <div className="mb-4 flex justify-center">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-primary to-primary/60">
                <PlatformLogo variant="white" className="h-6 w-6" />
              </div>
            </div>
            <CardTitle className="text-2xl">Two-Factor Authentication</CardTitle>
            <CardDescription>
              Enter the 6-digit code from your authenticator app to continue.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <NonProdMfaCodeBanner />
            <form onSubmit={handleVerify} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="mfa-code">Verification Code</Label>
                <Input
                  id="mfa-code"
                  placeholder="000000"
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  maxLength={6}
                  className="text-center font-mono text-2xl tracking-widest"
                  autoFocus
                  autoComplete="one-time-code"
                />
              </div>

              <Button
                type="submit"
                className="w-full"
                disabled={code.length !== 6 || isVerifying}
              >
                {isVerifying ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Verifying...
                  </>
                ) : (
                  'Verify'
                )}
              </Button>

              <Button
                type="button"
                variant="ghost"
                className="w-full text-muted-foreground"
                onClick={handleCancel}
                disabled={isVerifying}
              >
                Cancel and Sign Out
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
